import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Public as CountriesIcon,
} from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';
import { Autocomplete, Checkbox, ListItemText } from '@mui/material';
import shippingMethodsService from '../services/shippingMethodsService';

export default function ShippingMethods() {
  const [shippingMethods, setShippingMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [countriesDialogOpen, setCountriesDialogOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    carrier: '',
    serviceCode: '',
    zone: 'france',
    name: '',
    description: '',
    price: '',
    estimatedDays: '',
    icon: '',
    isAvailable: true,
    requiresPickupPoint: false,
    sortOrder: 0,
  });

  useEffect(() => {
    loadShippingMethods();
    loadAvailableCountries();
  }, []);

  const loadAvailableCountries = async () => {
    try {
      const data = await shippingMethodsService.getAvailableCountries();
      setAvailableCountries(data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des pays:', error);
    }
  };

  const handleOpenCountriesDialog = async (method) => {
    setSelectedMethod(method);
    try {
      const data = await shippingMethodsService.getCountries(method.id);
      setSelectedCountries(data.data || []);
      setCountriesDialogOpen(true);
    } catch (error) {
      console.error('Erreur lors du chargement des pays du mode:', error);
    }
  };

  const handleSaveCountries = async () => {
    if (!selectedMethod) return;
    try {
      await shippingMethodsService.updateCountries(selectedMethod.id, selectedCountries);
      setCountriesDialogOpen(false);
      alert('Pays mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour des pays:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const loadShippingMethods = async () => {
    try {
      setLoading(true);
      const data = await shippingMethodsService.getAll();
      setShippingMethods(data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des modes de livraison:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (method = null) => {
    if (method) {
      setEditingMethod(method);
      setFormData({
        code: method.code,
        carrier: method.carrier || '',
        serviceCode: method.serviceCode || '',
        zone: method.zone || 'france',
        name: method.name,
        description: method.description || '',
        price: method.price,
        estimatedDays: method.estimatedDays || '',
        icon: method.icon || '',
        isAvailable: method.isAvailable,
        requiresPickupPoint: method.requiresPickupPoint,
        sortOrder: method.sortOrder,
      });
    } else {
      setEditingMethod(null);
      setFormData({
        code: '',
        carrier: '',
        serviceCode: '',
        zone: 'france',
        name: '',
        description: '',
        price: '',
        estimatedDays: '',
        icon: '',
        isAvailable: true,
        requiresPickupPoint: false,
        sortOrder: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMethod(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingMethod) {
        await shippingMethodsService.update(editingMethod.id, formData);
      } else {
        await shippingMethodsService.create(formData);
      }
      handleCloseDialog();
      loadShippingMethods();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde du mode de livraison');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce mode de livraison ?')) {
      try {
        await shippingMethodsService.delete(id);
        loadShippingMethods();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du mode de livraison');
      }
    }
  };

  const handleToggleAvailability = async (id) => {
    try {
      await shippingMethodsService.toggleAvailability(id);
      loadShippingMethods();
    } catch (error) {
      console.error('Erreur lors du changement de disponibilité:', error);
      alert('Erreur lors du changement de disponibilité');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  if (loading && shippingMethods.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Modes de livraison</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Ajouter un mode de livraison
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Prix</TableCell>
                  <TableCell>Délai</TableCell>
                  <TableCell>Icône</TableCell>
                  <TableCell>Point relais</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Ordre</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shippingMethods.map((method) => (
                  <TableRow key={method.id}>
                    <TableCell>
                      <code>{method.code}</code>
                    </TableCell>
                    <TableCell>{method.name}</TableCell>
                    <TableCell>{method.description}</TableCell>
                    <TableCell>
                      <strong>{parseFloat(method.price).toFixed(2)}€</strong>
                    </TableCell>
                    <TableCell>{method.estimatedDays}</TableCell>
                    <TableCell style={{ fontSize: '24px' }}>{method.icon}</TableCell>
                    <TableCell>
                      {method.requiresPickupPoint ? (
                        <Chip label="Oui" color="primary" size="small" />
                      ) : (
                        <Chip label="Non" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={method.isAvailable ? <ActiveIcon /> : <InactiveIcon />}
                        label={method.isAvailable ? 'Actif' : 'Inactif'}
                        color={method.isAvailable ? 'success' : 'default'}
                        onClick={() => handleToggleAvailability(method.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>{method.sortOrder}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="info"
                        onClick={() => handleOpenCountriesDialog(method)}
                        title="Gérer les pays"
                      >
                        <CountriesIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(method)}
                        title="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(method.id)}
                        title="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog pour créer/éditer */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMethod ? 'Modifier le mode de livraison' : 'Ajouter un mode de livraison'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                helperText="Code unique (ex: pickup, chronopost)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Transporteur"
                name="carrier"
                value={formData.carrier}
                onChange={handleChange}
                helperText="Sélectionner un transporteur (optionnel)"
              >
                <MenuItem value="">Aucun</MenuItem>
                <MenuItem value="colissimo">Colissimo</MenuItem>
                <MenuItem value="chronopost">Chronopost</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code Service"
                name="serviceCode"
                value={formData.serviceCode}
                onChange={handleChange}
                helperText="Ex: A2P, DOM, DOS, CHRONO13, CHRONO18"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Zone"
                name="zone"
                value={formData.zone}
                onChange={handleChange}
                required
              >
                <MenuItem value="france">France Métropolitaine</MenuItem>
                <MenuItem value="outremer">Outre-Mer (DOM-TOM)</MenuItem>
                <MenuItem value="ue">Union Européenne</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Prix (€)"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
                required
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Délai estimé"
                name="estimatedDays"
                value={formData.estimatedDays}
                onChange={handleChange}
                helperText="Ex: 24h, 2-3 jours"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Icône (emoji)"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                helperText="Ex: 🚚, 📦, ⚡"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Ordre d'affichage"
                name="sortOrder"
                type="number"
                value={formData.sortOrder}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isAvailable}
                    onChange={handleChange}
                    name="isAvailable"
                  />
                }
                label="Disponible"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requiresPickupPoint}
                    onChange={handleChange}
                    name="requiresPickupPoint"
                  />
                }
                label="Nécessite un point relais"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingMethod ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour gérer les pays */}
      <Dialog open={countriesDialogOpen} onClose={() => setCountriesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Pays autorisés - {selectedMethod?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              options={availableCountries}
              disableCloseOnSelect
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              groupBy={(option) => {
                if (option.zone === 'france') return '🇫🇷 France';
                if (option.zone === 'outremer') return '🏝️ Outre-Mer (DOM-TOM)';
                if (option.zone === 'ue') return '🇪🇺 Union Européenne';
                return 'Autres';
              }}
              value={availableCountries.filter(c => selectedCountries.includes(c.code))}
              onChange={(event, newValue) => {
                setSelectedCountries(newValue.map(v => v.code));
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} />
                  <ListItemText primary={`${option.name} (${option.code})`} />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Sélectionner les pays"
                  placeholder="Rechercher un pays..."
                />
              )}
            />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {selectedCountries.length} pays sélectionné(s)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCountriesDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleSaveCountries} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

